/**
 * Returns a new array with unique elements in the same order as the original array.
 * This can be implemented in one line as array.filter((element, i, arr) => i === arr.indexOf(element)),
 * but it has O(n^2) running time. The implementation below has O(n) running time.
 * @param array to get unique elements from.
 * @param keyOrCallback used to determine uniqueness of elements in the array.
 */
export const uniqueBy = (array, keyOrCallback) => {
    let criterion;
    if (typeof keyOrCallback === 'string') {
        criterion = (element) => (element === null || element === undefined ? element : element[keyOrCallback]);
    }
    else {
        criterion = keyOrCallback;
    }
    const uniqueValues = new Set();
    const orderedUniqueElements = [];
    array.forEach((element) => {
        if (!uniqueValues.has(criterion(element))) {
            orderedUniqueElements.push(element);
            uniqueValues.add(criterion(element));
        }
    });
    return orderedUniqueElements;
};
/**
 * Determines whether two arrays contain different elements, regardless of order.
 *
 * @param arrayOne - The first array to compare.
 * @param arrayTwo - The second array to compare.
 * @returns `true` if the arrays have different lengths, or contain different elements;
 *          `false` if they contain the same elements, regardless of the order.
 *
 * Note: This implementation only works correctly for types that can be
 * directly compared using strict equality (e.g., numbers, strings, or references to the same objects).
 */
export const arraysAreDifferent = (arrayOne, arrayTwo) => {
    if (arrayOne.length !== arrayTwo.length) {
        return true;
    }
    const setOne = new Set(arrayOne);
    const setTwo = new Set(arrayTwo);
    return Array.from(setOne).some((item) => !setTwo.has(item));
};
